import React from 'react';
import { Button } from './ui/button';
import { useNavigate } from 'react-router-dom';
import { 
  CalendarBlank, 
  Users, 
  CurrencyDollar,
  Clock,
  CheckCircle,
  ChartLineUp,
  Briefcase,
  ArrowRight,
  ShieldCheck
} from '@phosphor-icons/react';

const ProjectManagement = () => {
  const navigate = useNavigate();

  const previewCapabilities = [
    {
      icon: <Briefcase className="h-6 w-6 text-[#2B4C6F]" weight="bold" />,
      bg: "bg-[#2B4C6F]/10",
      title: "Track Every Project",
      desc: "From initial quote to final walkthrough"
    },
    {
      icon: <CheckCircle className="h-6 w-6 text-emerald-600" weight="bold" />,
      bg: "bg-emerald-100 dark:bg-emerald-900/30",
      title: "Hit Your Deadlines",
      desc: "Milestone tracking keeps jobs on schedule"
    },
    {
      icon: <ChartLineUp className="h-6 w-6 text-purple-600" weight="bold" />,
      bg: "bg-purple-100 dark:bg-purple-900/30",
      title: "See Real Progress",
      desc: "Visual dashboards show exactly where you stand"
    },
    {
      icon: <Clock className="h-6 w-6 text-amber-600" weight="bold" />,
      bg: "bg-amber-100 dark:bg-amber-900/30",
      title: "Save Hours Weekly",
      desc: "Less paperwork, more time on the job site"
    }
  ];

  return (
    <section className="py-24 bg-[#F0F0F2] dark:bg-neutral-900" data-testid="project-management-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 bg-[#2B4C6F] text-white rounded-full px-4 py-2 mb-4 border-2 border-black shadow-[2px_2px_0px_#000]">
            <ChartLineUp className="h-4 w-4" weight="bold" />
            <span className="text-sm font-bold">Project Dashboard</span>
          </div>
          <h2 className="text-4xl lg:text-5xl font-bold text-neutral-900 dark:text-white mb-4">
            Run Jobs Like a Pro
          </h2>
          <p className="text-lg text-neutral-600 dark:text-neutral-300 max-w-3xl mx-auto">
            Keep every project organized with built-in tracking tools. Know what's happening, who's assigned, and what's coming up next.
          </p>
        </div>

        {/* Capability Grid */}
        <div className="grid md:grid-cols-4 gap-6 mb-12">
          {previewCapabilities.map((cap, idx) => (
            <div 
              key={idx} 
              className="bg-white dark:bg-neutral-800 rounded-xl p-6 border-2 border-black dark:border-neutral-700 shadow-[4px_4px_0px_#000] dark:shadow-[4px_4px_0px_#2B4C6F] hover:translate-y-[-4px] hover:shadow-[4px_8px_0px_#000] dark:hover:shadow-[4px_8px_0px_#2B4C6F] transition-all duration-300"
              data-testid={`capability-${idx}`}
            >
              <div className={`w-12 h-12 ${cap.bg} rounded-xl flex items-center justify-center mb-4 border-2 border-black dark:border-neutral-600`}>
                {cap.icon}
              </div>
              <p className="text-lg font-bold text-neutral-900 dark:text-white mb-2">{cap.title}</p>
              <p className="text-sm text-neutral-600 dark:text-neutral-400">{cap.desc}</p>
            </div>
          ))}
        </div>

        {/* Dashboard Preview Card */}
        <div className="bg-white dark:bg-neutral-800 rounded-xl border-2 border-black dark:border-neutral-700 shadow-[6px_6px_0px_#000] dark:shadow-[6px_6px_0px_#2B4C6F] p-8">
          <div className="flex flex-col lg:flex-row gap-8 items-center">
            {/* Left side - Feature description */}
            <div className="flex-1">
              <h3 className="text-2xl font-bold text-neutral-900 dark:text-white mb-4">Your Command Center</h3>
              <p className="text-neutral-600 dark:text-neutral-300 mb-6">
                One dashboard to manage all your active jobs. Track budgets, monitor crew assignments, and never miss a deadline again.
              </p>
              
              <ul className="space-y-4 mb-8">
                {[
                  { icon: <CheckCircle className="h-4 w-4 text-emerald-600" weight="bold" />, bg: "bg-emerald-100 dark:bg-emerald-900/30", title: "Visual Progress Tracking", desc: "See completion percentages at a glance" },
                  { icon: <Users className="h-4 w-4 text-[#2B4C6F]" weight="bold" />, bg: "bg-[#2B4C6F]/10", title: "Crew Management", desc: "Assign workers and track availability" },
                  { icon: <CurrencyDollar className="h-4 w-4 text-amber-600" weight="bold" />, bg: "bg-amber-100 dark:bg-amber-900/30", title: "Budget Control", desc: "Stay on top of project costs in real time" },
                  { icon: <ShieldCheck className="h-4 w-4 text-purple-600" weight="bold" />, bg: "bg-purple-100 dark:bg-purple-900/30", title: "Escrow Protection", desc: "Secure milestone-based payments" }
                ].map((item, idx) => (
                  <li key={idx} className="flex items-start gap-3">
                    <div className={`w-6 h-6 ${item.bg} rounded-full flex items-center justify-center flex-shrink-0 mt-0.5`}>
                      {item.icon}
                    </div>
                    <div>
                      <p className="font-semibold text-neutral-900 dark:text-white">{item.title}</p>
                      <p className="text-sm text-neutral-600 dark:text-neutral-400">{item.desc}</p>
                    </div>
                  </li>
                ))}
              </ul>

              <Button 
                onClick={() => navigate('/signup?type=contractor')}
                size="lg" 
                className="bg-[#2B4C6F] hover:bg-[#4A7DA6] text-white h-14 border-2 border-black shadow-[4px_4px_0px_#000] active:translate-y-1 active:shadow-none transition-all font-bold"
                data-testid="try-dashboard-btn"
              >
                Try the Dashboard Free
                <ArrowRight className="ml-2 h-5 w-5" weight="bold" />
              </Button>
            </div>

            {/* Right side - Simple visual representation */}
            <div className="flex-1 w-full">
              <div className="bg-[#2B4C6F]/10 dark:bg-[#2B4C6F]/20 rounded-xl p-6 border-2 border-black dark:border-[#2B4C6F]">
                <div className="flex items-center justify-between mb-4">
                  <h4 className="font-bold text-neutral-900 dark:text-white">Sample Project View</h4>
                  <span className="text-xs bg-[#2B4C6F] text-white px-2 py-1 rounded-full border border-black font-bold">Preview</span>
                </div>
                
                {/* Mini project cards showing structure */}
                <div className="space-y-3">
                  {[
                    { name: "Kitchen Renovation", status: "On Track", statusColor: "bg-emerald-500", progress: 75, due: "Apr 15", crew: "3 crew" },
                    { name: "Deck Installation", status: "In Progress", statusColor: "bg-[#2B4C6F]", progress: 40, due: "May 1", crew: "2 crew" },
                    { name: "Bathroom Remodel", status: "Planning", statusColor: "bg-purple-500", progress: 10, due: "Jun 20", crew: "TBD" }
                  ].map((project, idx) => (
                    <div key={idx} className="bg-white dark:bg-neutral-700 rounded-lg p-4 border-2 border-black dark:border-neutral-600 shadow-[2px_2px_0px_#000] dark:shadow-[2px_2px_0px_#2B4C6F]">
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-bold text-neutral-900 dark:text-white">{project.name}</span>
                        <span className={`text-xs ${project.statusColor} text-white px-2 py-0.5 rounded-full font-bold`}>{project.status}</span>
                      </div>
                      <div className="w-full bg-neutral-200 dark:bg-neutral-600 rounded-full h-2 mb-2">
                        <div className={`${project.statusColor} h-2 rounded-full`} style={{width: `${project.progress}%`}}></div>
                      </div>
                      <div className="flex items-center gap-4 text-xs text-neutral-500 dark:text-neutral-400">
                        <span className="flex items-center gap-1"><CalendarBlank className="h-3 w-3" weight="bold" /> Due: {project.due}</span>
                        <span className="flex items-center gap-1"><Users className="h-3 w-3" weight="bold" /> {project.crew}</span>
                      </div>
                    </div>
                  ))}
                </div>
                
                <p className="text-xs text-neutral-500 dark:text-neutral-400 text-center mt-4">This is a preview. Your actual dashboard will show your real projects.</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ProjectManagement;
